/**
 * ПРОДАЖ-розбивка Етап 4 (2026-06-03): Дашборд 4 показників наявності.
 *
 * Власник 2026-06-03 у плані: «створити окрему аналітику для наявності
 * товарів — 4 показника: Залишок / В дорозі / Резерв / Попит».
 *
 * 2026-08-22: канон Норми #29 — .card + InfiniteDocGrid (серверна
 * пагінація/сортування по whitelist), фільтри рядком під назвою.
 * Агрегатні показники — з сервера по ВСІЙ вибірці (не по сторінці).
 */
import { useMemo, useState } from 'react';
import { useOrgFilter, pickDefaultOrg } from '@/store/defaults';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import {
  reports,
  type InventoryAnalyticsRow,
  type InventoryAnalyticsTotals,
} from '@/api/documents';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import DatePopover from '@/components/DatePopover';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';


function endOfToday(): string {
  const d = new Date();
  d.setHours(23, 59, 59, 0);
  const tz = -d.getTimezoneOffset();
  const sign = tz >= 0 ? '+' : '-';
  const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${sign}${pad(tz / 60)}:${pad(tz % 60)}`;
}

const fmtQty = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 4 });


export default function InventoryAnalyticsPage() {
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // Орг за замовчуванням з шапки (override при ручному виборі).
  const [orgId, setOrgId] = useOrgFilter();
  const [onDate, setOnDate] = useState<string>(() => endOfToday());
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [totals, setTotals] = useState<InventoryAnalyticsTotals | null>(null);

  if (!orgId && orgList.data?.items[0]) setOrgId(pickDefaultOrg(orgList.data.items)!.id);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', orgId],
    queryFn: () => refs.warehouses(orgId),
    enabled: !!orgId,
  });

  const { datasource, total } = useInfiniteGridDatasource<InventoryAnalyticsRow>(
    async (limit, offset, sort) => {
      if (!orgId) return { items: [], total: 0 };
      const page = await reports.inventoryAnalytics({
        organization_id: orgId,
        on_date: onDate,
        warehouse_id: warehouseId || undefined,
        q: search || undefined,
        sort_by: sort?.sort_by,
        sort_dir: sort?.sort_dir,
        limit, offset,
      });
      setTotals(page.totals);
      return { items: page.rows, total: page.total };
    },
    [orgId, onDate, warehouseId, search],
  );

  // Color cells based on values
  const numCell = (v: string, color: string, bold = false) => (
    <span style={{ fontVariantNumeric: 'tabular-nums', color, fontWeight: bold ? 600 : undefined }}>
      {fmtQty(v)}
    </span>
  );

  const cols = useMemo<ColDef<InventoryAnalyticsRow>[]>(() => [
    { field: 'nomenclature_code', headerName: 'Код', width: 110 },
    { field: 'nomenclature_name', headerName: 'Номенклатура', flex: 1, minWidth: 220 },
    {
      field: 'uom_short', headerName: 'Од.', width: 70,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: InventoryAnalyticsRow }) =>
        p.data ? <span style={{ color: 'var(--text-secondary)' }}>{p.data.uom_short ?? ''}</span> : '',
    },
    { field: 'warehouse_name', headerName: 'Склад', width: 150 },
    {
      field: 'stock', headerName: 'Залишок', width: 120,
      cellStyle: { textAlign: 'right' },
      cellRenderer: (p: { data?: InventoryAnalyticsRow }) =>
        p.data ? numCell(p.data.stock, '#0f172a') : '',
    },
    {
      field: 'in_transit', headerName: 'В дорозі', width: 120,
      cellStyle: { textAlign: 'right' },
      cellRenderer: (p: { data?: InventoryAnalyticsRow }) =>
        p.data ? numCell(p.data.in_transit, '#2563eb') : '',
    },
    {
      field: 'reserved', headerName: 'Резерв', width: 120,
      cellStyle: { textAlign: 'right' },
      cellRenderer: (p: { data?: InventoryAnalyticsRow }) =>
        p.data ? numCell(p.data.reserved, '#ca8a04') : '',
    },
    {
      field: 'demanded', headerName: 'Попит', width: 120,
      cellStyle: { textAlign: 'right' },
      cellRenderer: (p: { data?: InventoryAnalyticsRow }) =>
        p.data ? numCell(p.data.demanded, '#9333ea') : '',
    },
    {
      field: 'available', headerName: 'Доступно', width: 130,
      cellStyle: { textAlign: 'right' },
      cellRenderer: (p: { data?: InventoryAnalyticsRow }) => {
        if (!p.data) return '';
        const v = Number(p.data.available);
        return numCell(p.data.available, v < 0 ? '#dc2626' : v === 0 ? '#64748b' : '#16a34a', true);
      },
    },
  ], []);

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>Монітор наявності товару<TitleTotal total={total} /></h2>
      </div>

      <div className="filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0 0 0.6rem', alignItems: 'center' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          style={{ maxWidth: 260 }}>
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>на дату</span>
        <div style={{ width: 170, flex: '0 0 auto' }}>
          <DatePopover
            withTime
            value={onDate.slice(0, 16)}
            onChange={(v) => setOnDate(v + ':00+02:00')}
          />
        </div>
        <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}
          style={{ maxWidth: 200 }}>
          <option value="">— Усі склади —</option>
          {warehousesQ.data?.map((w) => (
            <option key={w.id} value={w.id}>{w.name}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Назва або код товару…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 220 }}
        />
      </div>

      {totals !== null && (total ?? 0) > 0 && (
        <div style={{
          padding: '0.75rem',
          background: 'var(--bg-soft-2)',
          borderRadius: 6,
          marginBottom: '0.75rem',
          fontSize: '0.9rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: '0.5rem',
        }}>
          <div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Записів</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>{total}</div>
          </div>
          <div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Σ Залишок</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem', color: 'var(--text)' }}>{fmtQty(totals.stock)}</div>
          </div>
          <div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Σ В дорозі</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#2563eb' }}>{fmtQty(totals.in_transit)}</div>
          </div>
          <div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Σ Резерв</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#ca8a04' }}>{fmtQty(totals.reserved)}</div>
          </div>
          <div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Σ Попит</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#9333ea' }}>{fmtQty(totals.demanded)}</div>
          </div>
          <div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Σ Доступно</div>
            <div style={{
              fontWeight: 600, fontSize: '1.1rem',
              color: Number(totals.available) < 0 ? '#dc2626' : '#16a34a',
            }}>
              {fmtQty(totals.available)}
            </div>
          </div>
        </div>
      )}

      <InfiniteDocGrid<InventoryAnalyticsRow>
        datasource={datasource}
        columnDefs={cols}
        storageKey="inventory-analytics"
      />

      <div style={{
        marginTop: '1rem',
        padding: '0.75rem',
        background: 'var(--badge-amber-bg)',
        borderLeft: '3px solid var(--badge-amber-bd)',
        fontSize: '0.85rem',
        color: 'var(--badge-amber-fg)',
      }}>
        <strong>Як читати показники:</strong>
        <ul style={{ margin: '0.3rem 0 0 1rem', padding: 0 }}>
          <li><strong>Залишок</strong> — фактично на складі (Надходження + / Реалізація -).</li>
          <li><strong>В дорозі</strong> — замовлено постачальникам, ще не отримано.</li>
          <li><strong>Резерв</strong> — зарезервовано під рахунки покупцям.</li>
          <li><strong>Попит</strong> — замовлено покупцями (Замовлення покупця), ще не відвантажено.</li>
          <li><strong>Доступно</strong> = Залишок − Резерв. <em>Це те, що можна продати зараз без резервації.</em></li>
        </ul>
      </div>
    </div>
  );
}
